import { Alert, Box, Button, CircularProgress, Grid, IconButton, LinearProgress, Pagination, Paper, styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { USER_NAME_ROLE } from "src/app/auth";
import { deleteAllSystemHealthLogs, getSystemHealthLogs } from "src/app/services";
import { SystemHealthLog } from "src/app/types";
import WarningIcon from '@mui/icons-material/Warning';
import api from "../../../app/api";
import CommonApi from "src/commonApi";
import { convertUTCDateToLocalTime, formatDateSafe, formatToUTC } from "src/app/utils/helper";
import { downloadFile } from "src/app/utils/functions";
import { Download } from "@mui/icons-material";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#e8e8ed",
        color: theme.palette.common.black,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const SystemHealthLogsPageContent = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [isResetLoading, setIsResetLoading] = useState<boolean>(false);
    const [logs, setLogs] = useState<SystemHealthLog[]>([]);
    const [page, setPage] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const [isDataAvailable, setIsDataAvailable] = useState<any>(undefined);
    const [cpuStatus, setCpuStatus] = useState("");
    const handleClearLogsClick = () => {
        deleteAllSystemHealthLogs().then(() => {
            enqueueSnackbar({
                message: "All system health logs cleared successfully.",
                variant: "success",
            });
            setLogs([]);
        }).catch((err) => {
            console.error("Error clearing system health logs: ", err);
            enqueueSnackbar({
                message: "Error clearing system health logs.",
                variant: "error",
            });
        });
    }
    const addSystemHealthLog = () => {
        setLoading(true);
        api
            .post("/system_health_logs/addSystemHealthLog.php", {
                created_by: sessionStorage.getItem(USER_NAME_ROLE) == "admin"
                    ? 1
                    : 2,
            })
            .then((res) => {
                setCpuStatus(res?.data?.data?.summary?.cpu_dos_status);
                if (res?.data?.data?.summary?.cpu_dos_status) {
                    setIsDataAvailable(res?.data?.data?.summary?.cpu_dos_status);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                enqueueSnackbar({
                    message: "Error while adding the status log",
                    variant: "error",
                });
                CommonApi.addAuditFailureLog({
                    type: "System status log",
                    reason: error?.Message,
                    created_by: sessionStorage.getItem(USER_NAME_ROLE) == "admin"
                        ? 1 : 2,
                })
                setLoading(false);
            });
    };
    const resetVibox = useCallback(() => {
        setIsResetLoading(true);
        api.get("/tools/restart-vbox.php")
            .then((res) => {
                enqueueSnackbar({
                    message: "Server is restarted, cpu load is back to normal",
                    variant: "success",
                });
                setIsDataAvailable("")
            })
            .catch((error) => {
                console.error(error);
                enqueueSnackbar({
                    message: "Error while restarting the server",
                    variant: "error",
                });
            })
            .finally(() => {
                setIsResetLoading(false);
            });
    }, []);
    useEffect(() => {
        setLoading(true);
        getSystemHealthLogs(page)
            .then((response) => {
                setLogs(response.data.data);
                setTotalLogs(response.data.total);
                addSystemHealthLog();
            })
            .catch((err) => {
                enqueueSnackbar({
                    message: "Error fetching system health logs.",
                    variant: "error",
                });
            })
            .finally(() => setLoading(false));
    }, [page]);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ my: loading ? 1 : 2 }}>{loading && <LinearProgress />}</Box>
            {isDataAvailable === "Warning" && (
                <Box sx={{ my: 1 }}>
                    <Alert
                        sx={{ display: "flex" }}
                        severity="error"
                        icon={false}
                        onClose={() => {
                            setIsDataAvailable(false);
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mr: 2 }}>
                            <Typography sx={{ mr: 1 }}>
                                <Tooltip
                                    title={
                                        <div>
                                            Your system is experiencing a high load or potential DoS activity.
                                            It is recommended to reboot the server to restore normal performance.
                                        </div>
                                    }
                                >
                                    <IconButton>
                                        <WarningIcon sx={{ color: "#fbc02d" }}></WarningIcon>
                                    </IconButton>
                                </Tooltip> High CPU Load Detected
                            </Typography>
                            <Button
                                size="small"
                                sx={{ mr: "18px" }}
                                variant="outlined"
                                color="error"
                                onClick={resetVibox}
                                disabled={isResetLoading}
                                startIcon={isResetLoading ? <CircularProgress size={10} /> : <></>}
                            >
                                Reboot Server
                            </Button>
                        </Box>
                    </Alert>
                </Box>
            )}
            {!loading && sessionStorage.getItem(USER_NAME_ROLE) === "admin" && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 2,
                    }}
                >
                    <Button
                        size="medium"
                        sx={{ mr: "18px" }}
                        variant="outlined"
                        onClick={handleClearLogsClick}
                    >
                        Clear Logs
                    </Button>
                </Box>
            )}

            <Grid container>
                <Grid item xs={12} lg={12}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 500 }} aria-label="customized table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell align="center">Logged At</StyledTableCell>
                                    <StyledTableCell align="center">
                                        User
                                    </StyledTableCell>
                                    <StyledTableCell align="center">CPU Status</StyledTableCell>
                                    <StyledTableCell align="center">File Integrity</StyledTableCell>
                                    <StyledTableCell align="center">Process Status</StyledTableCell>
                                    <StyledTableCell align="center">File System</StyledTableCell>
                                    <StyledTableCell align="center">Audit Logs</StyledTableCell>
                                    <StyledTableCell align="center">Download</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.length === 0 && (
                                    <StyledTableRow>
                                        <StyledTableCell align="center" colSpan={7}>
                                            {loading ? "Loading..." : "No logs."}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                                {logs.map((row, i) => (
                                    <StyledTableRow key={i}>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {formatToUTC(row.created_at)} {!/^\d{10}$/.test(String(row.created_at)) ? "UTC" : ""}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.created_by !== null ? (row.created_by === 1 ? "Admin" : "Client") : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.cpu_dos_status ? row.cpu_dos_status : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.file_integrity_status ? row.file_integrity_status : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.process_status ? row.process_status : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.filesystem_status ? row.filesystem_status : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.audit_log_status ? row.audit_log_status : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <IconButton
                                                onClick={() => {
                                                    const content = `
                                                    Logged At: ${formatToUTC(row.created_at)} ${!/^\d{10}$/.test(String(row.created_at)) ? "UTC" : ""}
                                                    User: ${row.created_by !== null ? (row.created_by === 1 ? "Admin" : "Client") : "-"}
                                                    CPU Status: ${row.cpu_dos_status ? row.cpu_dos_status : "-"}
                                                    File Integrity: ${row.file_integrity_status ? row.file_integrity_status : "-"}
                                                    Process Status: ${row.process_status ? row.process_status : "-"}
                                                    File System: ${row.filesystem_status ? row.filesystem_status : "-"}
                                                    Audit Logs: ${row.audit_log_status ? row.audit_log_status : "-"}
                                                    `.replace(/^\s+/gm, "").trim();

                                                    downloadFile(
                                                        `system-health-log-${row.created_at}.txt`,
                                                        content,
                                                        "text/plain"
                                                    );
                                                }}
                                                aria-label="Export txt"
                                                color="primary"
                                                title="Download File"
                                            >
                                                <Download />
                                            </IconButton>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "20px",
                        }}
                    >
                        <Pagination
                            count={Math.ceil(totalLogs / 10)}
                            shape="rounded"
                            size="large"
                            page={page}
                            onChange={(e, newPage) => setPage(newPage)}
                            color="primary"
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SystemHealthLogsPageContent;